import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { z } from 'zod';

export interface Ok<T> {
  resultType: 'ok';
  value: T;
}

export interface Err<E extends BaseError> {
  resultType: 'err';
  error: E;
}

export type Result<T, E extends BaseError> = Ok<T> | Err<E>;

export function okResult<T>(value: T): Ok<T> {
  return {
    resultType: 'ok',
    value,
  };
}

export function errResult<E extends BaseError>(error: E): Err<E> {
  const capturedTrace = new Error().stack;
  // turn the trace into a string[]
  const trace = capturedTrace ? capturedTrace.split('\n') : [];
  // Remove the first line, which is the error message
  trace.shift();
  // Remove the line about errResult
  trace.shift();
  // Now turn each line into an array item
  const stackTrace = trace.map((line) => line.trim());

  // Always keep track of the first and the last stack trace.
  const stackTraces: string[][] = [];

  const errorShape = z.object({
    stackTraces: z.array(z.array(z.string())).optional(),
  });

  const safeParseResult = errorShape.safeParse(error);

  stackTraces[0] =
    safeParseResult.success &&
    safeParseResult.data.stackTraces &&
    safeParseResult.data.stackTraces.length > 0
      ? safeParseResult.data.stackTraces[0]
      : stackTrace;

  const newError: E = {
    ...error,
    stackTraces,
  };

  return {
    resultType: 'err',
    error: newError,
  };
}

export interface WithMeta<T, M> {
  value: T;
  meta: M;
}

export const withMeta = <T, M>(value: T, meta: M): WithMeta<T, M> => ({
  value,
  meta,
});

export const isErr = <T, E extends BaseError>(
  res: Result<T, E>,
): res is Err<E> => res.resultType === 'err';

export const isOk = <T, E extends BaseError>(res: Result<T, E>): res is Ok<T> =>
  res.resultType === 'ok';

export type AnyResult = Result<unknown, BaseError>;
